import { queryClient } from "@api/queryClient";
import {
  describeSoftphoneError,
  fetchBrowserRegistration,
  pairBrowserEndpoint,
} from "@phone/browserEndpointClient";
import { getInstallationId } from "@phone/browserInstallation";
import { phoneSetupKeys } from "@phone/phoneSetupQueries";
import {
  BROADCAST_SUPPORTED,
  coordinationName,
  LOCKS_SUPPORTED,
  type OwnershipMessage,
  TAB_ID,
} from "@phone/softphoneOwnership";
import { type INotification, TelnyxRTC } from "@telnyx/webrtc";
import { create } from "zustand";

/**
 * The browser as the staff line: brian dials the paired Telnyx credential over
 * WebRTC and skips the press-1 gate for it, so answering is the confirmation.
 * Module-level so navigation never drops the registration or a call.
 *
 * A ring is answered unasked only when its leg names the placed or picked-up
 * call (`ringCallUuid`); any other waits as `incomingRing`. brian rings before
 * the `/outbound-calls` response lands, so `markPlacedCall` reconciles a ring
 * held before admission.
 *
 * @telnyx/webrtc 2.27.10 device facts:
 * - `setAudioSettings({ micId })` only sets the session's `getUserMedia`
 *   constraints, which each incoming call snapshots on creation; the speaker
 *   can only be set once the call's `<audio>` exists, after `answer()`.
 * - `setAudioInDevice`/`setAudioOutDevice` need a concrete id; a live call can
 *   only move to "Browser default" via the synthetic `"default"` device.
 * - `setAudioInDevice` never rejects on a `getUserMedia` failure; it
 *   dispatches a `"userMediaError"` notification instead.
 * - `setAudioOutDevice` never throws; it resolves `false` on failure.
 *
 * Only one tab per (clinic, user, installation) may register, since Telnyx
 * would have every connected client answer the same ring; see
 * `startSoftphoneOwnership`. A non-owner tab's `status` stays `"off"`.
 */

export type SoftphoneStatus = "off" | "connecting" | "ready" | "error";

/** `"unavailable"`: no `navigator.locks`, so this tab acts as owner unconditionally. */
export type SoftphoneOwnership = "owner" | "non-owner" | "unavailable";

type SoftphoneCallState = "ringing" | "active" | "ending";

interface SoftphoneCall {
  state: SoftphoneCallState;
  muted: boolean;
}

interface AudioDevice {
  id: string;
  label: string;
}

/** A ring not answered unasked: an inbound one waiting on Answer or Decline, or the placed call's leg that rang before admission named the call. */
export interface IncomingRing {
  id: string;
  /** Null when withheld. */
  remoteNumber: string | null;
  /** From `ringCallUuid`. */
  callUuid: string | null;
}

interface SoftphoneState {
  status: SoftphoneStatus;
  error: string | null;
  endpointId: string | null;
  call: SoftphoneCall | null;
  /** The call placed through this browser; its card shows the softphone panel. */
  placedCallUuid: string | null;
  /** The call answered from its slip (`answerIncoming`). */
  answeredCallUuid: string | null;
  /** The held call awaiting brian's ring-back (`expectPickup`); lapses after `PICKUP_WINDOW_MS`. */
  pickupCallUuid: string | null;
  incomingRing: IncomingRing | null;
  /** The call whose WebRTC leg died without Hang up being pressed. */
  droppedCallUuid: string | null;
  microphones: AudioDevice[];
  speakers: AudioDevice[];
  /** The stored preference; null = "Browser default". */
  microphoneId: string | null;
  speakerId: string | null;
  /** What the SDK actually applied; diverges from the preference only on failure. */
  activeSpeakerId: string | null;
  microphoneError: string | null;
  speakerError: string | null;
  ownership: SoftphoneOwnership;
  ownerSince: number | null;
  takeoverRefused: string | null;
}

const ENABLED_KEY = "brian.softphone.enabled";
const MIC_KEY = "brian.softphone.mic";
const SPEAKER_KEY = "brian.softphone.speaker";

/** Chrome/Edge's synthetic OS-default device; Firefox and Safari don't enumerate one. */
const BROWSER_DEFAULT_DEVICE_ID = "default";

const SINK_ID_SUPPORTED =
  typeof HTMLMediaElement !== "undefined" &&
  typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId ===
    "function";

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string | null): void => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage refused: the choice lasts until the next reload.
  }
};

export const useSoftphoneStore = create<SoftphoneState>(() => ({
  status: "off",
  error: null,
  endpointId: null,
  call: null,
  placedCallUuid: null,
  answeredCallUuid: null,
  pickupCallUuid: null,
  incomingRing: null,
  droppedCallUuid: null,
  microphones: [],
  speakers: [],
  microphoneId: readStorage(MIC_KEY),
  speakerId: readStorage(SPEAKER_KEY),
  activeSpeakerId: null,
  microphoneError: null,
  speakerError: null,
  ownership: LOCKS_SUPPORTED ? "non-owner" : "unavailable",
  ownerSince: null,
  takeoverRefused: null,
}));

/** False on Safari. */
export const SPEAKER_SELECTION_SUPPORTED = SINK_ID_SUPPORTED;

const set = useSoftphoneStore.setState;
const get = useSoftphoneStore.getState;

type RtcCall = NonNullable<INotification["call"]>;

let client: TelnyxRTC | null = null;
let activeCall: RtcCall | null = null;
let audioElement: HTMLAudioElement | null = null;
/** Bumped on every enable/disable so a stale async step can't act on a new client. */
let generation = 0;
/** Set by Hang up so the ensuing hangup event reads as chosen, not dropped. */
let hangupRequested = false;
/**
 * `Call._onMediaError` hangs up with NORMAL_CLEARING right after a
 * `userMediaError`; this marks that hangup as a drop.
 */
let mediaErrorHangup = false;
let pending: Promise<void> | null = null;
/** Settles {@link pending} from `teardown()`, which off()s the events that would. */
let settleRegister: (() => void) | null = null;
let tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;
const installationId = getInstallationId();
let ownershipKey: string | null = null;
let ownershipChannel: BroadcastChannel | null = null;
/** Resolving this releases the Web Lock to the next queued tab. */
let releaseOwnershipLock: (() => void) | null = null;
let ownershipAbort: AbortController | null = null;
/**
 * Mirrors brian's `encode_client_state`/`encode_outbound_client_state`
 * (`modules/phone/media.py`) and `BROWSER_CALL_UUID_HEADER`
 * (`modules/telnyx/voice.py`): the call is `pc` in the base64 JSON
 * `client_state` and also a SIP header, since Telnyx doesn't forward
 * `client_state` to a SIP leg. The caller number can't identify the call: both
 * dials come from the clinic's own DID.
 */
const ringCallUuid = (call: RtcCall): string | null =>
  clientStateCallUuid(call.options.clientState) ??
  headerCallUuid(call.options.customHeaders);

const CALL_UUID_HEADER = "x-brian-call-uuid";

const clientStateCallUuid = (state: string | undefined): string | null => {
  if (!state) return null;
  try {
    const payload: unknown = JSON.parse(atob(state));
    if (typeof payload !== "object" || payload === null) return null;
    const { pc } = payload as { pc?: unknown };
    return typeof pc === "string" && pc !== "" ? pc : null;
  } catch {
    return null;
  }
};

const headerCallUuid = (
  headers: RtcCall["options"]["customHeaders"],
): string | null => {
  const value = headers
    ?.find((header) => header.name.toLowerCase() === CALL_UUID_HEADER)
    ?.value.trim();
  return value ? value : null;
};

const carriedCallUuid = (): string | null => {
  const { answeredCallUuid, placedCallUuid } = get();
  return answeredCallUuid ?? placedCallUuid;
};

/** Played through a hidden <audio> so setSinkId applies; autoplay refusal leaves it silent. */
let ringtone: {
  ctx: AudioContext;
  element: HTMLAudioElement;
  cadence: ReturnType<typeof setInterval>;
} | null = null;

const RING_FREQUENCIES_HZ = [440, 480];
const RING_GAIN = 0.15;
const RING_ON_S = 2;
const RING_PERIOD_MS = 6_000;

const startRingtone = (): void => {
  if (ringtone || typeof AudioContext === "undefined") return;
  const ctx = new AudioContext();
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const destination = ctx.createMediaStreamDestination();
  gain.connect(destination);
  for (const frequency of RING_FREQUENCIES_HZ) {
    const oscillator = ctx.createOscillator();
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    oscillator.start();
  }
  const burst = () => {
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(RING_GAIN, now);
    gain.gain.setValueAtTime(0, now + RING_ON_S);
  };
  burst();
  const cadence = setInterval(burst, RING_PERIOD_MS);
  const element = document.createElement("audio");
  element.autoplay = true;
  element.hidden = true;
  element.setAttribute("data-testid", "softphone-ringtone");
  element.srcObject = destination.stream;
  document.body.appendChild(element);
  const speaker = get().speakerId;
  if (speaker && SINK_ID_SUPPORTED)
    void element.setSinkId(speaker).catch(() => {});
  void ctx.resume().catch(() => {});
  void element.play().catch(() => {});
  ringtone = { ctx, element, cadence };
};

const stopRingtone = (): void => {
  if (!ringtone) return;
  const { ctx, element, cadence } = ringtone;
  ringtone = null;
  clearInterval(cadence);
  element.pause();
  element.srcObject = null;
  element.remove();
  void ctx.close().catch(() => {});
};

const remoteAudio = (): HTMLAudioElement => {
  if (audioElement) return audioElement;
  const el = document.createElement("audio");
  el.autoplay = true;
  el.hidden = true;
  el.setAttribute("data-testid", "softphone-audio");
  document.body.appendChild(el);
  audioElement = el;
  return el;
};

/** Telnyx causes for an ordinary hangup; a missing cause is ordinary too. */
const NORMAL_HANGUP_CAUSES = new Set([
  "NORMAL_CLEARING",
  "USER_BUSY",
  "ORIGINATOR_CANCEL",
  "CALL_REJECTED",
]);

const dropCall = (rtcCall: RtcCall) => {
  const { call } = get();
  const lost =
    !hangupRequested &&
    call !== null &&
    call.state === "active" &&
    (mediaErrorHangup ||
      !(!rtcCall.cause || NORMAL_HANGUP_CAUSES.has(rtcCall.cause)));
  activeCall = null;
  hangupRequested = false;
  mediaErrorHangup = false;
  stopRingtone();
  set({
    call: null,
    incomingRing: null,
    droppedCallUuid: lost ? carriedCallUuid() : null,
  });
};

const MICROPHONE_MISSING_NOTE =
  "The saved microphone isn't connected. Using the default for now.";
const SPEAKER_MISSING_NOTE =
  "The saved speaker isn't connected. Using the default for now.";

const toDevices = (infos: MediaDeviceInfo[]): AudioDevice[] =>
  infos
    .filter((info) => info.deviceId !== "")
    .map((info, index) => ({
      id: info.deviceId,
      label: info.label || `Device ${index + 1}`,
    }));

/**
 * Re-read after every answer, since labels only appear once mic permission is
 * granted. Flags a saved device that no longer enumerates, and clears only
 * that note, never a switch failure's.
 */
const refreshAudioDevices = async (): Promise<void> => {
  const rtc = client;
  if (!rtc) return;
  try {
    const [mics, outs] = await Promise.all([
      rtc.getAudioInDevices(),
      rtc.getAudioOutDevices(),
    ]);
    const microphones = toDevices(mics);
    const speakers = toDevices(outs);
    set({ microphones, speakers });
    const { microphoneId, speakerId, microphoneError, speakerError } = get();
    if (microphoneId && !microphones.some((d) => d.id === microphoneId)) {
      set({ microphoneError: microphoneError ?? MICROPHONE_MISSING_NOTE });
    } else if (microphoneError === MICROPHONE_MISSING_NOTE) {
      set({ microphoneError: null });
    }
    if (speakerId && !speakers.some((d) => d.id === speakerId)) {
      set({ speakerError: speakerError ?? SPEAKER_MISSING_NOTE });
    } else if (speakerError === SPEAKER_MISSING_NOTE) {
      set({ speakerError: null });
    }
  } catch {
    // Enumeration refused: the pickers just stay on "default".
  }
};

/** Null: nothing the SDK can apply to an already-answered call. */
const resolveConcreteDeviceId = (
  preferred: string | null,
  devices: AudioDevice[],
): string | null => {
  if (preferred) return preferred;
  return devices.some((d) => d.id === BROWSER_DEFAULT_DEVICE_ID)
    ? BROWSER_DEFAULT_DEVICE_ID
    : null;
};

/** A `getUserMedia` failure arrives as `userMediaError`, not here. */
const applyMicrophone = async (call: RtcCall): Promise<void> => {
  const { microphoneId, microphones } = get();
  const target = resolveConcreteDeviceId(microphoneId, microphones);
  if (!target) {
    set({
      microphoneError:
        "Browser default will apply on the next call; this one is already using a device.",
    });
    return;
  }
  try {
    await call.setAudioInDevice(target, get().call?.muted ?? false);
    if (activeCall !== call) return;
    set({ microphoneError: null });
  } catch (error) {
    if (activeCall !== call) return;
    set({
      microphoneError:
        error instanceof Error ? error.message : "Couldn't switch microphones.",
    });
  }
};

const applySpeaker = async (call: RtcCall): Promise<void> => {
  const { speakerId, speakers } = get();
  const target = resolveConcreteDeviceId(speakerId, speakers);
  if (!target) {
    set({ activeSpeakerId: null, speakerError: null });
    return;
  }
  if (!SINK_ID_SUPPORTED) {
    set({
      speakerError: "This browser can't switch speakers; using the default.",
    });
    return;
  }
  const ok = await call.setAudioOutDevice(target);
  if (activeCall !== call) return;
  if (ok) set({ activeSpeakerId: target, speakerError: null });
  else
    set({
      speakerError: "Couldn't switch speakers. Using the default for now.",
    });
};

const answerRingingCall = async (call: RtcCall): Promise<void> => {
  try {
    await call.answer();
  } catch {
    if (activeCall?.id !== call.id) return;
    // Mic denied or absent: Brian would keep ringing an endpoint that can't pick up.
    const carried = carriedCallUuid();
    void call.hangup();
    teardown();
    set({
      status: "error",
      error:
        "Brian couldn't use this computer's microphone. Allow microphone access, then turn the headset back on.",
      droppedCallUuid: carried,
    });
    return;
  }
  if (activeCall?.id !== call.id) return;
  void applySpeaker(call);
  void refreshAudioDevices();
};

const answerPlacedRing = (call: RtcCall): void => {
  set({
    call: { state: "ringing", muted: false },
    droppedCallUuid: null,
  });
  void answerRingingCall(call);
};

const answerPickupRing = (call: RtcCall, callUuid: string): void => {
  clearPickupWindow();
  set({
    pickupCallUuid: null,
    answeredCallUuid: callUuid,
    call: { state: "ringing", muted: false },
    droppedCallUuid: null,
  });
  void answerRingingCall(call);
};

/** Long enough for the resume dial; short enough that a much later ring for the call is offered, not auto-answered. */
const PICKUP_WINDOW_MS = 30_000;
let pickupWindow: ReturnType<typeof setTimeout> | null = null;

const clearPickupWindow = () => {
  if (pickupWindow !== null) clearTimeout(pickupWindow);
  pickupWindow = null;
};

const onNotification = (notification: INotification) => {
  if (notification.type === "userMediaError") {
    const failed = notification.call;
    if (activeCall && (!failed || failed.id === activeCall.id)) {
      mediaErrorHangup = get().call?.state === "active";
    }
    set({
      microphoneError:
        notification.error?.message ?? "Couldn't use that microphone.",
    });
    return;
  }
  if (notification.type !== "callUpdate" || !notification.call) return;
  const call = notification.call;
  switch (call.state) {
    case "ringing": {
      if (activeCall && activeCall.id !== call.id) return;
      if (!client) return;
      activeCall = call;
      const callUuid = ringCallUuid(call);
      if (callUuid !== null && callUuid === get().placedCallUuid) {
        answerPlacedRing(call);
        return;
      }
      if (callUuid !== null && callUuid === get().pickupCallUuid) {
        answerPickupRing(call, callUuid);
        return;
      }
      set({
        incomingRing: {
          id: call.id,
          remoteNumber: call.options.remoteCallerNumber ?? null,
          callUuid,
        },
      });
      startRingtone();
      return;
    }
    case "active":
      if (activeCall?.id !== call.id) return;
      set({ call: { state: "active", muted: call.isAudioMuted } });
      return;
    case "hangup":
    case "destroy":
    case "purge":
      if (activeCall?.id === call.id) dropCall(call);
      return;
    default:
      return;
  }
};

/** Read before `teardown()`, which off()s the listener that would see the hangup. */
const lostCarriedCall = (): string | null => {
  const { call } = get();
  return call !== null && call.state !== "ending" ? carriedCallUuid() : null;
};

const teardown = () => {
  generation += 1;
  activeCall = null;
  hangupRequested = false;
  mediaErrorHangup = false;
  stopRingtone();
  stopTokenRefresh();
  set({
    call: null,
    incomingRing: null,
    endpointId: null,
    activeSpeakerId: null,
    microphoneError: null,
    speakerError: null,
  });
  // Mid-connect, the handlers that settle `register()` are off()d; unsettled,
  // every later `enableSoftphone()` would await it forever.
  const settle = settleRegister;
  settleRegister = null;
  pending = null;
  const old = client;
  client = null;
  if (old) {
    old.off("telnyx.ready");
    old.off("telnyx.error");
    old.off("telnyx.notification");
    old.off("telnyx.socket.close");
    void old.disconnect();
  }
  settle?.();
};

/** Keeps brian's `last_registered_at` fresh so its stale-endpoint cleanup never reaps an idle open browser. */
const TOKEN_REFRESH_MS = 20 * 60 * 1000;

/**
 * Throttles resume re-mints: brian's `BROWSER_TOKEN_RATE` (20/min per IP) is
 * shared by a whole clinic behind one NAT, and `register()` needs it too.
 */
const RESUME_REMINT_MIN_MS = 5 * 60 * 1000;

let lastMintAt = 0;

/** Only bumps `last_registered_at`; the live client keeps its original token. */
const remintToken = async (): Promise<void> => {
  if (!client) return;
  try {
    await fetchBrowserRegistration(installationId);
    lastMintAt = Date.now();
  } catch {
    // Left for the next attempt.
  }
};

const startTokenRefresh = () => {
  stopTokenRefresh();
  tokenRefreshTimer = setInterval(() => void remintToken(), TOKEN_REFRESH_MS);
};

const stopTokenRefresh = () => {
  if (tokenRefreshTimer) clearInterval(tokenRefreshTimer);
  tokenRefreshTimer = null;
};

const register = async (persist: boolean): Promise<void> => {
  if (persist) writeStorage(ENABLED_KEY, "1");
  const mine = ++generation;
  set({ status: "connecting", error: null });
  let token: string;
  try {
    const endpoint = await pairBrowserEndpoint(installationId);
    const registration = await fetchBrowserRegistration(installationId);
    lastMintAt = Date.now();
    if (mine !== generation) return;
    set({ endpointId: endpoint.id });
    token = registration.token;
    // Otherwise "This computer" is missing from `useOutboundLines` until stale.
    void queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all });
  } catch (error) {
    if (mine !== generation) return;
    set({ status: "error", error: describeSoftphoneError(error) });
    return;
  }

  const rtc = new TelnyxRTC({
    login_token: token,
    // A closed tab must not leave Brian ringing a browser that is gone.
    hangupOnBeforeUnload: true,
  });
  rtc.remoteElement = remoteAudio();
  rtc.speaker = get().speakerId ?? "";
  // Before `connect()`: each incoming call snapshots these on creation.
  try {
    await rtc.setAudioSettings({ micId: get().microphoneId ?? undefined });
  } catch {
    // `answer()` falls back to the browser's default track.
  }
  if (mine !== generation) return;
  client = rtc;
  await new Promise<void>((resolve) => {
    settleRegister = resolve;
    rtc.on("telnyx.ready", () => {
      if (client !== rtc) return;
      set({ status: "ready", error: null });
      void refreshAudioDevices();
      startTokenRefresh();
      resolve();
    });
    rtc.on("telnyx.error", (event) => {
      if (client !== rtc) return;
      const message = event.error?.message
        ? `Headset connection failed: ${event.error.message}`
        : "Headset connection failed.";
      // A lingering `client` would make `enableSoftphone` a no-op forever.
      const lost = lostCarriedCall();
      teardown();
      set({ status: "error", error: message, droppedCallUuid: lost });
      resolve();
    });
    rtc.on("telnyx.socket.close", () => {
      if (client !== rtc) return;
      // No reconnect: re-enabling mints a fresh token.
      const lost = lostCarriedCall();
      teardown();
      set({
        status: "error",
        error: "The headset connection dropped.",
        droppedCallUuid: lost,
      });
      resolve();
    });
    rtc.on("telnyx.notification", onNotification);
    rtc.connect();
  });
  // After a teardown a fresh registration may own it.
  if (mine === generation) settleRegister = null;
};

/**
 * Resolves once Telnyx reports ready or the store is in "error". Concurrent
 * callers await the in-flight registration, since `client` is set mid-connect.
 * `persist: false` skips recording the opt-in, so the clinic-wide default
 * (`SoftphoneBridge`) doesn't outlive the clinic turning it off.
 */
export const enableSoftphone = async ({
  persist = true,
}: { persist?: boolean } = {}): Promise<void> => {
  if (pending) return pending;
  if (client) return;
  if (get().ownership === "non-owner") {
    set({
      status: "error",
      error: "Another tab owns this computer's headset.",
    });
    return;
  }
  const run = register(persist);
  pending = run;
  try {
    await run;
  } finally {
    if (pending === run) pending = null;
  }
};

export const disableSoftphone = (): void => {
  writeStorage(ENABLED_KEY, null);
  suspendSoftphone();
};

/** Unregister but keep the stored opt-in. */
const suspendSoftphone = (): void => {
  teardown();
  set({ status: "off", error: null, call: null });
};

const resumeSoftphone = (): void => {
  if (readStorage(ENABLED_KEY) === "1") void enableSoftphone();
};

const announceOwner = (since: number) => {
  ownershipChannel?.postMessage({
    type: "owner-here",
    tabId: TAB_ID,
    since,
  } satisfies OwnershipMessage);
};

const becomeOwner = (): void => {
  const since = Date.now();
  set({
    ownership: "owner",
    ownerSince: since,
    takeoverRefused: null,
  });
  announceOwner(since);
  resumeSoftphone();
};

const loseOwnership = (): void => {
  suspendSoftphone();
  releaseOwnershipLock = null;
  if (get().ownership === "owner")
    set({ ownership: "non-owner", ownerSince: null });
};

/**
 * Every non-owner tab stays queued so the next one takes over when the owner
 * goes. `steal` lands a granted takeover on the requester, not the queue head.
 */
const queueForOwnership = (key: string, name: string, steal = false): void => {
  if (ownershipKey !== key || !LOCKS_SUPPORTED) return;
  const controller = new AbortController();
  ownershipAbort?.abort();
  ownershipAbort = controller;
  const options: LockOptions = steal
    ? { mode: "exclusive", steal: true }
    : { mode: "exclusive", signal: controller.signal };
  void navigator.locks
    .request(name, options, () => {
      if (ownershipKey !== key || ownershipAbort !== controller) return;
      becomeOwner();
      return new Promise<void>((resolve) => {
        releaseOwnershipLock = resolve;
      });
    })
    .catch(() => {
      // Aborted or stolen: `finally` settles the state.
    })
    .finally(() => {
      if (ownershipAbort !== controller) return;
      loseOwnership();
      if (ownershipKey === key) queueForOwnership(key, name);
    });
};

/** No-op on the same key; a different principal tears down the previous coordination first. */
export const startSoftphoneOwnership = (principalKey: string): void => {
  const key = `${principalKey}:${installationId}`;
  if (ownershipKey === key) return;
  stopSoftphoneOwnership();
  ownershipKey = key;

  if (!LOCKS_SUPPORTED) {
    set({ ownership: "unavailable", ownerSince: null });
    resumeSoftphone();
    return;
  }

  const name = coordinationName(principalKey, installationId);
  set({
    ownership: "non-owner",
    ownerSince: null,
    takeoverRefused: null,
  });

  if (BROADCAST_SUPPORTED) {
    const channel = new BroadcastChannel(name);
    ownershipChannel = channel;
    channel.onmessage = (event: MessageEvent<OwnershipMessage>) => {
      const message = event.data;
      if (get().ownership === "owner") {
        if (message.type === "focus-request") window.focus();
        else if (message.type === "takeover-request") {
          if (get().call !== null) {
            channel.postMessage({
              type: "takeover-refused",
              toTabId: message.fromTabId,
              reason: "This tab has an active call.",
            } satisfies OwnershipMessage);
          } else {
            // Down first so two clients never overlap; the requester steals the lock.
            suspendSoftphone();
            channel.postMessage({
              type: "takeover-granted",
              toTabId: message.fromTabId,
            } satisfies OwnershipMessage);
          }
        } else if (message.type === "query-owner") {
          announceOwner(get().ownerSince ?? Date.now());
        }
        return;
      }
      if (message.type === "owner-here") {
        set({ ownerSince: message.since });
      } else if (
        message.type === "takeover-granted" &&
        message.toTabId === TAB_ID
      ) {
        queueForOwnership(key, name, true);
      } else if (
        message.type === "takeover-refused" &&
        message.toTabId === TAB_ID
      ) {
        set({ takeoverRefused: message.reason });
      }
    };
    channel.postMessage({ type: "query-owner" } satisfies OwnershipMessage);
  }

  queueForOwnership(key, name);
};

/** Always takes the registration down: the previous user's client must never survive into the next session. */
export const stopSoftphoneOwnership = (): void => {
  ownershipKey = null;
  const controller = ownershipAbort;
  ownershipAbort = null;
  controller?.abort();
  const release = releaseOwnershipLock;
  releaseOwnershipLock = null;
  release?.();
  suspendSoftphone();
  ownershipChannel?.close();
  ownershipChannel = null;
  set({
    ownership: LOCKS_SUPPORTED ? "non-owner" : "unavailable",
    ownerSince: null,
    takeoverRefused: null,
  });
};

/** Browsers may refuse a background tab's `window.focus()`. */
export const focusOwningTab = (): void => {
  ownershipChannel?.postMessage({
    type: "focus-request",
    fromTabId: TAB_ID,
  } satisfies OwnershipMessage);
};

/** Granted only when the owner has no active call. */
export const requestHeadsetTakeover = (): void => {
  set({ takeoverRefused: null });
  ownershipChannel?.postMessage({
    type: "takeover-request",
    fromTabId: TAB_ID,
  } satisfies OwnershipMessage);
};

export const hangupSoftphoneCall = (): void => {
  const call = activeCall;
  if (!call) return;
  hangupRequested = true;
  set({ call: { state: "ending", muted: call.isAudioMuted } });
  void call.hangup();
};

export const toggleSoftphoneMute = (): void => {
  const call = activeCall;
  if (!call) return;
  call.toggleAudioMute();
  set({ call: { state: "active", muted: call.isAudioMuted } });
};

/** brian rings before answering the POST, so a held `incomingRing` naming this call is answered now. */
export const markPlacedCall = (callUuid: string): void => {
  set({
    placedCallUuid: callUuid,
    answeredCallUuid: null,
    droppedCallUuid: null,
  });
  const ring = activeCall;
  const held = get().incomingRing;
  if (!ring || !held || held.id !== ring.id || held.callUuid !== callUuid)
    return;
  stopRingtone();
  set({ incomingRing: null });
  answerPlacedRing(ring);
};

/** Call before `POST /phone-calls/{uuid}/resume`; same held-ring reconciliation as `markPlacedCall`. */
export const expectPickup = (callUuid: string): void => {
  clearPickupWindow();
  set({ pickupCallUuid: callUuid });
  pickupWindow = setTimeout(() => cancelPickup(callUuid), PICKUP_WINDOW_MS);
  const ring = activeCall;
  const held = get().incomingRing;
  if (!ring || !held || held.id !== ring.id || held.callUuid !== callUuid)
    return;
  stopRingtone();
  set({ incomingRing: null });
  answerPickupRing(ring, callUuid);
};

export const cancelPickup = (callUuid: string): void => {
  if (get().pickupCallUuid !== callUuid) return;
  clearPickupWindow();
  set({ pickupCallUuid: null });
};

export const answerIncoming = (callUuid: string): void => {
  const ring = activeCall;
  if (!ring || get().incomingRing?.id !== ring.id) return;
  stopRingtone();
  set({
    incomingRing: null,
    answeredCallUuid: callUuid,
    call: { state: "ringing", muted: false },
    droppedCallUuid: null,
  });
  void answerRingingCall(ring);
};

/** Hang up this leg only; brian carries on with whatever is still ringing. */
export const declineIncoming = (): void => {
  const ring = activeCall;
  if (!ring || get().incomingRing?.id !== ring.id) return;
  stopRingtone();
  hangupRequested = true;
  set({ incomingRing: null });
  void ring.hangup();
};

export const setMicrophone = (deviceId: string | null): void => {
  writeStorage(MIC_KEY, deviceId);
  set({ microphoneId: deviceId, microphoneError: null });
  void client
    ?.setAudioSettings({ micId: deviceId ?? undefined })
    .catch(() => {});
  const call = activeCall;
  if (call) void applyMicrophone(call);
};

export const setSpeaker = (deviceId: string | null): void => {
  writeStorage(SPEAKER_KEY, deviceId);
  set({ speakerId: deviceId, speakerError: null });
  if (client) client.speaker = deviceId ?? "";
  if (ringtone && SINK_ID_SUPPORTED)
    void ringtone.element.setSinkId(deviceId ?? "").catch(() => {});
  const call = activeCall;
  if (call) void applySpeaker(call);
  else set({ activeSpeakerId: deviceId });
};

if (typeof navigator !== "undefined" && navigator.mediaDevices) {
  navigator.mediaDevices.addEventListener("devicechange", () => {
    void refreshAudioDevices();
  });
}

// A backgrounded tab's timers can sleep past `TOKEN_REFRESH_MS`.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastMintAt < RESUME_REMINT_MIN_MS) return;
    void remintToken();
  });
}
